// ============================================================================
// Modality Layouts
// ============================================================================
/**
 * Default modality layouts for the viewer
 * These are used as fallbacks when no hanging protocol matches
 */
export const DEFAULT_MODALITY_LAYOUTS = [
    {
        modality: 'CR',
        displayName: 'Computed Radiography',
        layout: { rows: 1, cols: 1 },
        defaultWLPreset: 'xr-default',
    },
    {
        modality: 'DX',
        displayName: 'Digital X-Ray',
        layout: { rows: 1, cols: 1 },
        defaultWLPreset: 'xr-default',
    },
    {
        modality: 'CT',
        displayName: 'CT',
        layout: { rows: 1, cols: 2 },
        defaultWLPreset: 'ct-soft',
    },
    {
        modality: 'MR',
        displayName: 'MRI',
        layout: { rows: 2, cols: 2 },
        defaultWLPreset: 'mr-default',
    },
    {
        modality: 'PT',
        displayName: 'PET',
        layout: { rows: 1, cols: 2 },
        defaultWLPreset: 'pet-default',
    },
    {
        modality: 'US',
        displayName: 'Ultrasound',
        layout: { rows: 1, cols: 1 },
    },
    {
        modality: 'NM',
        displayName: 'Nuclear Medicine',
        layout: { rows: 1, cols: 2 },
    },
    {
        modality: 'XA',
        displayName: 'X-Ray Angiography',
        layout: { rows: 1, cols: 1 },
    },
    {
        modality: 'RF',
        displayName: 'Fluoroscopy',
        layout: { rows: 1, cols: 1 },
    },
    {
        modality: 'OT',
        displayName: 'Other',
        layout: { rows: 1, cols: 1 },
    },
];
// ============================================================================
// Tool Bindings
// ============================================================================
export const DEFAULT_TOOL_BINDINGS = {
    left: 'WindowLevel',
    right: 'Zoom',
    middle: 'Pan',
    wheel: 'scroll',
    invertScroll: false,
};
// ============================================================================
// Overlay Settings
// ============================================================================
export const DEFAULT_OVERLAY_SETTINGS = {
    // Patient info
    showPatientName: true,
    showPatientId: true,
    showPatientBirthDate: false,
    showPatientSex: true,
    showPatientSpecies: true,
    showPatientBreed: true,
    // Study info
    showStudyDate: true,
    showStudyDescription: true,
    showAccessionNumber: true,
    showReferringVet: true,
    showInstitution: false,
    // Series/Image info
    showSeriesDescription: true,
    showSeriesNumber: false,
    showImageNumber: true,
    showSliceThickness: true,
    showWindowLevel: true,
    showLaterality: true,
    // Viewport decorations
    showOrientationMarkers: true,
    showScaleRuler: true,
    // Display
    textSize: 'medium',
    textColor: '#ffffff',
};
// ============================================================================
// W/L Presets
// ============================================================================
export const DEFAULT_WL_PRESETS = [
    // CT
    {
        id: 'ct-soft',
        name: 'Soft Tissue',
        windowWidth: 400,
        windowCenter: 40,
        modality: 'CT',
        isFavorite: true,
        isCustom: false,
    },
    {
        id: 'ct-lung',
        name: 'Lung',
        windowWidth: 1500,
        windowCenter: -600,
        modality: 'CT',
        isFavorite: true,
        isCustom: false,
    },
    {
        id: 'ct-bone',
        name: 'Bone',
        windowWidth: 2500,
        windowCenter: 480,
        modality: 'CT',
        isFavorite: true,
        isCustom: false,
    },
    {
        id: 'ct-brain',
        name: 'Brain',
        windowWidth: 80,
        windowCenter: 40,
        modality: 'CT',
        isFavorite: false,
        isCustom: false,
    },
    {
        id: 'ct-abdomen',
        name: 'Abdomen',
        windowWidth: 350,
        windowCenter: 50,
        modality: 'CT',
        isFavorite: false,
        isCustom: false,
    },
    // MR
    {
        id: 'mr-default',
        name: 'MR Default',
        windowWidth: 400,
        windowCenter: 200,
        modality: 'MR',
        isFavorite: true,
        isCustom: false,
    },
    // X-Ray
    {
        id: 'xr-default',
        name: 'X-Ray Default',
        windowWidth: 2048,
        windowCenter: 1024,
        modality: 'CR',
        isFavorite: true,
        isCustom: false,
    },
];
// ============================================================================
// Echo Measurement Panel
// ============================================================================
export const DEFAULT_ECHO_MEASUREMENT_PANEL = {
    // Sensible first-open position (top-right of a typical viewer), user-draggable.
    x: 24,
    y: 96,
    mode: '2d',
    collapsed: false,
    linearUnit: 'cm',
    velocityUnit: 'm/s',
    tierView: 'standard',
    collapsedGroups: [],
    extendedGroupsSeeded: false,
};
export const DEFAULT_WORKLIST_COLUMNS = [
    { key: 'pid', visible: true },
    { key: 'patientName', visible: true },
    { key: 'description', visible: true },
    { key: 'dateTime', visible: true },
    { key: 'modality', visible: true },
    { key: 'series', visible: true },
    { key: 'images', visible: true },
    { key: 'accession', visible: true },
    { key: 'institution', visible: true },
    { key: 'notify', visible: true },
];
// ============================================================================
// Storage
// ============================================================================
/**
 * Storage key for viewer settings in localStorage
 */
export const VIEWER_SETTINGS_STORAGE_KEY = 'newlumen_viewer_settings';
/**
 * Current settings version for migration support
 */
export const VIEWER_SETTINGS_VERSION = 1;
